import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getProductFiles, uploadProductFile, deleteProductFile } from "@/lib/api";
import { DirectUpload } from "@rails/activestorage";
import { toast } from "sonner";

type ProductFilesProps = {
  site: { slug: string };
  product: { id: number };
};

const ProductFiles: React.FC<ProductFilesProps> = ({ site, product }) => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileTitle, setFileTitle] = useState("");
  const [fileDescription, setFileDescription] = useState("");
  const [fileType, setFileType] = useState<string>("document");
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const {
    data: files,
    isLoading: filesLoading,
    isError: filesError,
    error: filesErrorObj,
  } = useQuery({
    queryKey: ["product-files", site.slug, product.id],
    queryFn: () => getProductFiles(site.slug, product.id),
    enabled: !!product,
  });

  const uploadMutation = useMutation({
    mutationFn: async (data: { file: File; title: string; description: string; file_type: string }) => {
      return new Promise((resolve, reject) => {
        const upload = new DirectUpload(data.file, "/rails/active_storage/direct_uploads");
        upload.create((error, blob) => {
          if (error) {
            reject(error);
          } else {
            uploadProductFile(site.slug, product.id, blob.signed_id, data.title, data.description, data.file_type)
              .then(resolve)
              .catch(reject);
          }
        });
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-files", site.slug, product.id] });
      setSelectedFile(null);
      setFileTitle("");
      setFileDescription("");
      setFileType("document");
      setShowUploadDialog(false);
      toast("File uploaded", {
        description: "The file was uploaded successfully.",
      });
    },
    onError: (error: any) => {
      toast("Upload failed", {
        description: error?.message || "Failed to upload file.",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (fileId: number) => deleteProductFile(site.slug, product.id, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-files", site.slug, product.id] });
      toast("File deleted", {
        description: "The file was deleted successfully.",
      });
    },
  });

  const handleUpload = () => {
    if (!selectedFile || !fileTitle || !fileType) {
      toast("Missing information", {
        description: "Please fill in all required fields.",
      });
      return;
    }
    uploadMutation.mutate({
      file: selectedFile,
      title: fileTitle,
      description: fileDescription,
      file_type: fileType,
    });
  };

  const getFileTypeIcon = (type: string) => {
    switch (type) {
      case "audio":
        return "🎵";
      case "video":
        return "🎬";
      case "pdf":
        return "📄";
      case "document":
        return "📝";
      case "image":
        return "🖼️";
      case "archive":
        return "📦";
      default:
        return "📎";
    }
  };

  return (
    <div>
      <div className="mb-4">
        <Button onClick={() => setShowUploadDialog(true)} className="mb-2">
          Upload File
        </Button>
        <p className="text-sm text-muted-foreground">
          Upload product files that customers will receive after purchase (PDFs, audio, video, etc.)
        </p>
      </div>

      {filesLoading ? (
        <div className="text-muted-foreground">Loading files...</div>
      ) : filesError ? (
        <Alert variant="destructive" className="mb-2">
          {(filesErrorObj as Error).message}
        </Alert>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {files && files.length > 0 ? (
            files.map((file: any) => (
              <Card key={file.id} className="p-4">
                <div className="flex items-start gap-4">
                  <div className="text-4xl">{getFileTypeIcon(file.file_type)}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{file.title}</h3>
                    {file.description && (
                      <p className="text-sm text-muted-foreground mt-1">{file.description}</p>
                    )}
                    <div className="text-xs text-muted-foreground mt-2">
                      <span className="mr-4">Type: {file.file_type}</span>
                      {file.file_data?.filename && <span className="mr-4">File: {file.file_data.filename}</span>}
                      {file.file_data?.byte_size && <span>Size: {(file.file_data.byte_size / (1024 * 1024)).toFixed(2)} MB</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {file.file_data?.url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(file.file_data.url, "_blank")}
                      >
                        Download
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-300"
                      onClick={() => deleteMutation.mutate(file.id)}
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="text-muted-foreground text-center py-8">
              No files uploaded yet.
            </div>
          )}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg w-full">
          <DialogHeader>
            <DialogTitle>Upload Product File</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="file-title">* Title</Label>
              <Input
                id="file-title"
                value={fileTitle}
                onChange={(e) => setFileTitle(e.target.value)}
                placeholder="Enter file title"
                required
              />
            </div>
            <div>
              <Label htmlFor="file-description">Description</Label>
              <Textarea
                id="file-description"
                value={fileDescription}
                onChange={(e) => setFileDescription(e.target.value)}
                placeholder="Enter file description (optional)"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="file-type">* File Type</Label>
              <Select value={fileType} onValueChange={setFileType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select file type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="archive">Archive (ZIP, RAR, etc.)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="file-input">* File</Label>
              <input
                id="file-input"
                type="file"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="block w-full mt-1 text-sm text-slate-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary file:text-primary-foreground
                  hover:file:bg-primary/90"
              />
              {selectedFile && (
                <p className="text-xs text-muted-foreground mt-1">
                  Selected: {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4 flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadDialog(false);
                setSelectedFile(null);
                setFileTitle("");
                setFileDescription("");
                setFileType("document");
              }}
              disabled={uploadMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploadMutation.isPending || !selectedFile || !fileTitle}>
              {uploadMutation.isPending ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductFiles;
